<script setup lang="ts">
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { ref, watch } from 'vue'
import { Loader } from 'lucide-vue-next'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { useUserStore } from '@/stores/user'
import { Permission } from '@/lib/permissions/v1'
import Button from '../ui/button/Button.vue'
import { DotsVerticalIcon, PlusIcon, UpdateIcon } from '@radix-icons/vue'
import { VocusNBNRouteName } from '@/modules/vocus-nbn/router'
import Add from './vocus-nbn/modals/Add.vue'
import Edit from './vocus-nbn/modals/Edit.vue'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { useGetVocusNbnServersQuery } from '@/queries/vocus-nbn'

const props = defineProps<{
    active: boolean
}>()

const { data: servers, isLoading, isRefetching, refetch } = useGetVocusNbnServersQuery()

const user = useUserStore()

const openAddModal = ref(false)
const openEditModal = ref(false)
const selectedServer = ref(null)

const add = (server) => {
    selectedServer.value = server
    openAddModal.value = true
}

const edit = (server) => {
    selectedServer.value = server
    openEditModal.value = true
}

watch(
    () => props.active,
    async () => {
        if (props.active) {
            await refetch()
        }
    }
)
</script>

<template>
    <AccordionItem value="vocusNBN" class="border-b-0">
        <AccordionTrigger
            class="group py-2 px-4 text-white hover:no-underline hover:bg-neutral-600/50 data-[state=open]:bg-zinc-800 justify-between"
        >
            <div class="flex items-center justify-between flex-1">
                <div class="flex items-center">
                    <div class="w-6">
                        <FontAwesomeIcon :icon="['fal', 'network-wired']" class="size-4!" />
                    </div>
                    <div class="ml-3 text-xs">Vocus NBN</div>
                </div>
            </div>
            <div class="flex items-center">
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Button
                                v-if="user.hasPermission(Permission.VocusMobileServerCreate)"
                                variant="icon"
                                size="icon-xs"
                                class="h-4 w-4 mr-2 hidden group-hover:flex"
                                @click.stop="add"
                            >
                                <PlusIcon class="h-4 w-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Add Vocus NBN server</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Button
                                variant="icon"
                                size="icon-xs"
                                class="h-4 w-4 mr-2 hidden group-hover:flex"
                                @click.stop="refetch"
                            >
                                <UpdateIcon
                                    class="h-3 w-3"
                                    :class="{ 'animate-spin': isLoading || isRefetching }"
                                />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Refresh</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </AccordionTrigger>
        <AccordionContent class="pb-0 text-xs text-white">
            <div
                v-if="isLoading || isRefetching"
                class="flex items-center space-x-2 pl-[52px] py-2"
            >
                <Loader class="mr-2 h-4 w-4 shrink-0 opacity-50 animate-spin" />
                <span>Loading...</span>
            </div>
            <div v-else class="flex flex-col">
                <template v-for="server in servers" :key="server.id">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <RouterLink
                                    :to="{
                                        name: VocusNBNRouteName.ServerServicesIndex,
                                        params: {
                                            serverId: server.id,
                                        },
                                    }"
                                    class="group flex items-center justify-between pl-[52px] py-2 pr-4 hover:bg-neutral-600/50 truncate"
                                >
                                    <div class="truncate">{{ server?.title }}</div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger as-child>
                                                        <Button
                                                            variant="icon"
                                                            size="icon-xs"
                                                            class="mr-2 opacity-0 group-hover:opacity-100!"
                                                        >
                                                            <DotsVerticalIcon class="h-3 w-3" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Actions</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem @click="edit(server)"
                                                >Edit</DropdownMenuItem
                                            >
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </RouterLink>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                                <p>{{ server?.title }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </template>
            </div>
        </AccordionContent>
        <Add v-model="openAddModal" />
        <Edit v-model="openEditModal" :server="selectedServer" />
    </AccordionItem>
</template>

<style scoped>
.router-link-exact-active {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>
